import React, { useCallback, useEffect, useRef, useState } from "react";

import Fade from "@material-ui/core/Fade";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";

// Tempo que o painel espera antes de fechar, para o mouse conseguir atravessar
// o vao entre o item e o painel sem que ele suma.
const CLOSE_DELAY_MS = 180;

// Tamanho unico dos icones da barra, por nivel de menu.
export const MENU_ICON_SIZE = 24;
export const SUBMENU_ICON_SIZE = 20;

const useStyles = makeStyles((theme) => ({
  popper: {
    // Acima do Drawer (zIndex.drawer) e da AppBar (zIndex.drawer + 1).
    zIndex: theme.zIndex.drawer + 2,
  },
  paper: {
    minWidth: 200,
    marginLeft: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    // O painel e portalizado no body, entao repete o espacamento e o tamanho
    // de icone usados no menu (ver menuRoot em MainListItems). Tudo aqui e
    // subitem, dai o tamanho unico.
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      marginRight: theme.spacing(2),
    },
    "& .MuiListItemIcon-root .MuiSvgIcon-root": {
      fontSize: SUBMENU_ICON_SIZE,
    },
  },
}));

/**
 * Painel flutuante exibido ao passar o mouse sobre um item do menu retraido.
 *
 * Usa Popper (e nao Popover) porque Popover monta um Modal com backdrop e trap
 * de foco, o que quebra a interacao por hover. O Popper e portalizado no body,
 * entao nao e cortado pelo overflow do Drawer retraido.
 */
const SidebarFlyout = ({ active, panel, children }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const timeoutRef = useRef(null);

  const open = Boolean(active && anchorEl);

  const cancelClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const closeNow = useCallback(() => {
    cancelClose();
    setAnchorEl(null);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    timeoutRef.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  }, [cancelClose]);

  const handleEnter = (event) => {
    if (!active) return;
    cancelClose();
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => cancelClose, [cancelClose]);

  // Se o menu deixa de estar retraido (ou o hover e desligado), some o painel.
  useEffect(() => {
    if (!active) closeNow();
  }, [active, closeNow]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeNow();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, closeNow]);

  return (
    <>
      <div onMouseEnter={handleEnter} onMouseLeave={scheduleClose}>
        {children}
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="right-start"
        className={classes.popper}
        modifiers={{
          preventOverflow: { enabled: true, boundariesElement: "viewport" },
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={120}>
            <Paper
              elevation={8}
              className={classes.paper}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              {typeof panel === "function"
                ? panel({ closeFlyout: closeNow })
                : panel}
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

/**
 * Rotulo lateral para itens sem submenu. Um Tooltip basta e custa bem menos
 * que um Popper.
 */
export const SidebarTooltip = ({ active, title, children }) => {
  if (!active || !title) return children;

  return (
    <Tooltip title={title} placement="right" enterDelay={200}>
      {children}
    </Tooltip>
  );
};

export default SidebarFlyout;
